import { Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, Observable, startWith, Subject, switchMap, tap } from 'rxjs';
import { AsyncState, toAsyncState } from '../models/async-state';
import { Period } from '../../core/constants/periods';

export interface RetryableAsyncStateOptions<T> {
  idSignal: Signal<string | undefined>;
  periodSignal?: Signal<Period>;
  retry$: Subject<void>;
  fetcher: (id: string, period: Period) => Observable<T>;
  onError?: (error: unknown) => void;
}

export function createRetryableAsyncState<T>(options: RetryableAsyncStateOptions<T>): Signal<AsyncState<T>> {
  const { idSignal, periodSignal, retry$, fetcher, onError } = options;

  const id$ = toObservable(idSignal);

  const combined$ = periodSignal
    ? combineLatest([id$, toObservable(periodSignal)])
    : id$.pipe(map(id => [id, 'DAY' as Period] as const));

  const data$ = retry$.pipe(
    startWith(undefined),
    switchMap(() => combined$),
    filter((params): params is [string, Period] => !!params[0]),
    switchMap(([id, period]) => fetcher(id, period).pipe(tap({ error: onError }), toAsyncState())),
  );

  return toSignal(data$, {
    initialValue: { status: 'loading' } as AsyncState<T>,
  });
}
